import React, { useEffect, useRef, useState } from "react";
import { message } from "antd";
import styles from "./emergencyContacts.module.scss";
import { addEmergencyContact } from "../../../../network";

const USER_ICON =
  "https://d1flzashw70bti.cloudfront.net/original/images/selfHelp/aecUser_1789463765983.svg";
const SHARE_ICON =
  "https://d1flzashw70bti.cloudfront.net/original/images/selfHelp/aecShare_1789463696283.svg";
const ADD_ICON =
  "https://d1flzashw70bti.cloudfront.net/original/images/selfHelp/AEC_1789463593467.svg";

const MAX_CONTACTS = 2;

// Older app builds ignore openContactPicker; if nothing comes back in this
// window we open the form so "Add" never looks dead.
const NATIVE_PICKER_TIMEOUT = 2 * 60 * 1000;

const formatPhone = value => {
  const digits = value.replace(/\D/g, "").slice(-10);
  return digits.length === 10
    ? `${digits.slice(0, 5)} ${digits.slice(5)}`
    : value;
};

// Chrome on Android only; everywhere else we fall back to typing it in.
const canPickContacts = () =>
  typeof navigator !== "undefined" &&
  navigator.contacts &&
  typeof navigator.contacts.select === "function";

const isInApp = () =>
  typeof window !== "undefined" &&
  (window.ReactNativeWebView ||
    (window.AndroidWebView &&
      typeof window.AndroidWebView.openContactPicker === "function"));

// Android delivers app messages on document, iOS on window, and either may
// send the payload as a JSON string or an object.
const parseAppMessage = event => {
  try {
    return typeof event.data === "string" ? JSON.parse(event.data) : event.data;
  } catch (error) {
    return null;
  }
};

const EmergencyContacts = props => {
  const { booking, onContactsUpdated } = props;

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [name, setName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [saving, setSaving] = useState(false);

  const contacts = booking.alternateEmergencyContactDetails || [];
  const canAddMore = contacts.length < MAX_CONTACTS;

  const resetForm = () => {
    setName("");
    setMobileNumber("");
    setIsFormOpen(false);
  };

  const nativePickerTimer = useRef(null);
  const onAppMessage = useRef(null);

  // If the picked entry is unusable (no name / not a 10 digit number) the form
  // opens prefilled so the user can correct it instead of starting over.
  const handlePickedContact = async (pickedName, pickedNumber) => {
    const saved = await saveContact(pickedName, pickedNumber);

    if (!saved) {
      setName(pickedName);
      setMobileNumber(pickedNumber.replace(/\D/g, "").slice(-10));
      setIsFormOpen(true);
    }
  };

  // Add opens the phone book straight away and saves the chosen contact.
  const pickFromPhonebook = async () => {
    let picked;

    try {
      picked = await navigator.contacts.select(["name", "tel"], {
        multiple: false
      });
    } catch (error) {
      // Picker failed to open — let them type the contact in instead.
      setIsFormOpen(true);
      return;
    }

    // Dismissed without choosing anyone.
    if (!picked || !picked.length) {
      return;
    }

    const pickedName = (picked[0].name && picked[0].name[0]) || "";
    const pickedNumber = (picked[0].tel && picked[0].tel[0]) || "";

    handlePickedContact(pickedName, pickedNumber);
  };

  // Inside the app the WebView has no contact picker, so the app opens the
  // native one and replies with a contactPicked message (ZCA-1665 / ZIA-1316).
  const pickFromAppPhonebook = () => {
    clearTimeout(nativePickerTimer.current);
    nativePickerTimer.current = setTimeout(
      () => setIsFormOpen(true),
      NATIVE_PICKER_TIMEOUT
    );

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage("openContactPicker");
      return;
    } else if (
      window.AndroidWebView &&
      typeof window.AndroidWebView.openContactPicker === "function"
    ) {
      window.AndroidWebView.openContactPicker();
      return;
    }
  };

  // Kept in a ref so the listener below always sees the latest booking.
  onAppMessage.current = event => {
    const data = parseAppMessage(event);

    if (!data) {
      return;
    }

    // The app acks as soon as its picker is up, so the fallback form does not
    // open behind it while the user is still choosing.
    if (data.type === "contactPickerOpened") {
      clearTimeout(nativePickerTimer.current);
      return;
    }

    if (data.type !== "contactPicked") {
      return;
    }

    clearTimeout(nativePickerTimer.current);

    if (data.cancelled) {
      setIsFormOpen(true);
      return;
    }

    handlePickedContact(data.name || "", data.tel || "");
  };

  useEffect(() => {
    const listener = event => onAppMessage.current(event);

    window.addEventListener("message", listener);
    document.addEventListener("message", listener);

    return () => {
      window.removeEventListener("message", listener);
      document.removeEventListener("message", listener);
      clearTimeout(nativePickerTimer.current);
    };
  }, []);

  const onAddClick = () => {
    if (isInApp()) {
      pickFromAppPhonebook();
    } else if (canPickContacts()) {
      pickFromPhonebook();
    } else {
      setIsFormOpen(true);
    }
  };

  const shareTrackingLink = async contact => {
    const link = booking.trackingUrl;

    if (!link) {
      message.error("Tracking link is not available yet");
      return;
    }

    const shareText = `Track my zingbus journey here: ${link}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: "zingbus tracking", text: shareText });
        return;
      } catch (error) {
        return;
      }
    }

    window.open(
      `https://wa.me/91${contact.mobileNo}?text=${encodeURIComponent(
        shareText
      )}`,
      "_blank"
    );
  };

  const saveContact = async (contactName, contactNumber) => {
    const digits = contactNumber.replace(/\D/g, "").slice(-10);

    if (!contactName.trim() || digits.length !== 10) {
      message.error("Enter a valid name and 10 digit mobile number");
      return false;
    }

    setSaving(true);

    try {
      const updated = await addEmergencyContact({
        bookingId: booking.id || booking._id,
        name: contactName.trim(),
        mobileNo: digits
      });

      // Fetch resolves null on a rejected add after it has already shown the
      // server's error, so only a real contact list counts as success.
      if (Array.isArray(updated)) {
        onContactsUpdated(updated);
        message.success("Emergency contact added");
        resetForm();
        setSaving(false);
        return true;
      }
    } catch (error) {
      message.error(error.message || "Could not add contact, please retry");
    }

    setSaving(false);
    return false;
  };

  return (
    <div className={styles.wrapper}>
      {isFormOpen ? (
        <div className={styles.header}>
          <span className={styles.title}>Add Emergency Contact</span>
          <span className={styles.subtitle}>
            Contacts once added cannot be edited or removed for the current trip
          </span>
        </div>
      ) : (
        <div className={styles.header}>
          <span className={styles.title}>Alternate Emergency Contact</span>
          <span className={styles.subtitle}>
            They will receive notifications in case of an emergency, and you can
            also share a tracking link with added contacts.
          </span>
        </div>
      )}

      <div className={styles.contacts}>
        {contacts.map(contact => (
          <div className={styles.contact} key={contact.mobileNo}>
            <div className={styles.contactInfo}>
              <img className={styles.avatar} src={USER_ICON} alt="" />
              <div className={styles.contactText}>
                <span className={styles.contactName}>{contact.name}</span>
                <span className={styles.contactPhone}>
                  {formatPhone(contact.mobileNo)}
                </span>
              </div>
            </div>
            <div
              className={styles.share}
              onClick={() => shareTrackingLink(contact)}
            >
              <img src={SHARE_ICON} alt="" />
              <span>Share</span>
            </div>
          </div>
        ))}

        {isFormOpen ? (
          <div className={styles.form}>
            <input
              className={styles.input}
              type="text"
              placeholder="Name"
              value={name}
              maxLength={40}
              onChange={event => setName(event.target.value)}
            />
            <input
              className={styles.input}
              type="tel"
              placeholder="Phone Number"
              value={mobileNumber}
              maxLength={10}
              onChange={event =>
                setMobileNumber(event.target.value.replace(/\D/g, ""))
              }
            />
            <div className={styles.formActions}>
              <div className={styles.cancel} onClick={resetForm}>
                <span>Cancel</span>
              </div>
              <div
                className={saving ? styles.saveDisabled : styles.save}
                onClick={saving ? null : () => saveContact(name, mobileNumber)}
              >
                <span>{saving ? "Adding..." : "Add Contact"}</span>
              </div>
            </div>
          </div>
        ) : null}

        {canAddMore && !isFormOpen ? (
          <div
            className={`${styles.addButton} ${saving ? styles.disabled : ""}`}
            onClick={saving ? null : onAddClick}
          >
            <img src={ADD_ICON} alt="" />
            <span>{saving ? "Adding..." : "Add Emergency Contact"}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EmergencyContacts;
